import { Component, Input, OnInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';

@Component({
    selector: 'app-map-preview',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div #mapContainer class="w-full h-full bg-gray-100 relative z-0">
        <div *ngIf="loading" class="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
    </div>
  `,
    styles: [`
    :host { display: block; height: 100%; width: 100%; }
  `]
})
export class MapPreviewComponent implements OnInit, OnDestroy {
    @Input() polyline!: string;
    @Input() distance_km?: number; // Actual route distance in km
    @ViewChild('mapContainer') mapContainer!: ElementRef;

    private map: L.Map | undefined;
    loading = true;

    ngOnInit() {
        // defer map init to ensure container is ready
        setTimeout(() => this.initMap(), 100);
    }

    initMap() {
        if (!this.polyline) return;

        // Decode polyline (simple version or import library, usually google.maps.geometry.encoding.decodePath or @mapbox/polyline)
        // For pure Leaflet without hefty deps, we can specific decode function or assume we use library.
        // Strava polylines are standard encoded strings.

        // I will include a simple decode function to avoid extra deps if possible, or use a known one.
        const coordinates = this.decode(this.polyline);

        if (!coordinates.length) {
            this.loading = false;
            return;
        }

        this.map = L.map(this.mapContainer.nativeElement, {
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false
        });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            maxZoom: 19
        }).addTo(this.map);

        const polylineLayer = L.polyline(coordinates, {
            color: '#fc4c02',
            weight: 3,
            opacity: 0.8
        }).addTo(this.map);

        // Use actual route distance if available, otherwise calculate from bounds
        let distance = this.distance_km || 0;

        if (!distance) {
            // Fallback: Calculate approximate distance from bounds
            const bounds = polylineLayer.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latDiff = Math.abs(ne.lat - sw.lat);
            const lngDiff = Math.abs(ne.lng - sw.lng);
            distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 111;
        }

        // Dynamic zoom based on ACTUAL distance - MUCH higher zoom for all routes
        let maxZoom = 17; // Default very high zoom
        if (distance > 100) maxZoom = 13;      // Very long routes (100+ km)
        else if (distance > 50) maxZoom = 14;  // Long routes (50-100 km)
        else if (distance > 20) maxZoom = 15;  // Medium routes (20-50 km)
        else if (distance > 10) maxZoom = 16;  // Short routes (10-20 km)
        // else maxZoom = 17 for very short routes (<10 km)

        console.log(`[MapPreview] Route distance: ${distance.toFixed(2)} km, maxZoom: ${maxZoom}`);

        this.map.fitBounds(polylineLayer.getBounds(), {
            padding: [10, 10],
            maxZoom: maxZoom
        });
        this.loading = false;
    }

    ngOnDestroy() {
        if (this.map) {
            this.map.remove();
        }
    }

    // Simple Polyline Decode Logic (Google encoded polyline algorithm)
    private decode(encoded: string): L.LatLngExpression[] {
        const points: L.LatLngExpression[] = [];
        let index = 0, len = encoded.length;
        let lat = 0, lng = 0;

        while (index < len) {
            let b, shift = 0, result = 0;
            do {
                b = encoded.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
            lat += dlat;

            shift = 0;
            result = 0;
            do {
                b = encoded.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
            lng += dlng;

            points.push([lat * 1e-5, lng * 1e-5]);
        }
        return points;
    }
}
